import { Link } from "react-router-dom";
import { ArrowRight, HandCoins, ShieldCheck, Share2, Sparkles, Leaf } from "lucide-react";
import LeaderboardSection from "@/components/LeaderboardSection";
import SpotlightSection from "@/components/SpotlightSection";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[var(--bone)] paper-noise">
      <header className="glass-nav sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-[var(--forest)] text-white grid place-items-center flex-shrink-0">
              <Leaf className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <div className="min-w-0">
              <div className="font-heading text-base sm:text-xl leading-tight text-[var(--forest)] truncate">CARING</div>
              <div className="eyebrow -mt-0.5 hidden sm:block">Programa de Referências</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="pill-btn pill-ghost" data-testid="landing-login-btn">Entrar</Link>
            <Link to="/register" className="pill-btn pill-primary" data-testid="landing-cta-register">
              Tenho convite <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="hero-arc" aria-hidden />
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-7 space-y-8">
            <div className="eyebrow">Referências CARING · Portugal</div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl text-[var(--ink)] leading-[1.02]">
              Recomende quem cuida.<br/>
              <span className="text-[var(--forest)]">Receba 15 €</span> por cada contratação.
            </h1>
            <p className="text-lg sm:text-xl text-[var(--ink-soft)] max-w-2xl leading-relaxed">
              Ligue pessoas de confiança às vagas da CARING. Quando um candidato que recomendou é contratado
              e conclui o período mínimo, recebe automaticamente <strong>15 € em Cartão Dá</strong>. Simples,
              transparente, pago até ao dia 8 do mês seguinte.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register" className="pill-btn pill-primary" data-testid="hero-cta-register">
                Tenho convite <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login" className="pill-btn pill-ghost" data-testid="hero-cta-login">Já tenho conta</Link>
            </div>
            <div className="text-sm text-[var(--ink-soft)]">
              Sem convite? <a href="mailto:admin@caring.pt?subject=Pedido%20de%20convite%20CARING%20Refer%C3%AAncias" className="text-[var(--forest)] underline" data-testid="request-invite-mailto">Peça o seu ao administrador</a>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-3 pt-6 text-sm text-[var(--ink-soft)]">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[var(--forest)]"/> Sem custos, sem vínculo laboral</div>
              <div className="flex items-center gap-2"><HandCoins className="w-4 h-4 text-[var(--terracotta)]"/> Pagamento por Cartão Dá</div>
              <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[var(--info)]"/> Automático de ponta a ponta</div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="absolute -inset-4 rounded-[32px] bg-gradient-to-br from-[rgba(217,108,74,0.15)] via-transparent to-[rgba(44,76,59,0.15)] blur-2xl -z-10" aria-hidden />
            <div className="card-surface p-8 relative">
              <div className="eyebrow">Prémio</div>
              <div className="font-heading text-6xl text-[var(--forest)] mt-2">15 €</div>
              <div className="text-[var(--ink-soft)]">por cada candidato contratado</div>
              <div className="mt-6 space-y-3">
                {[
                  { label: "1", txt: "Registe-se em 2 minutos" },
                  { label: "2", txt: "Assine digitalmente o protocolo" },
                  { label: "3", txt: "Partilhe vagas com um link único" },
                  { label: "4", txt: "Receba o prémio até dia 8 do mês seguinte" },
                ].map((s) => (
                  <div key={s.label} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[var(--muted)] grid place-items-center font-semibold text-[var(--forest)]">{s.label}</div>
                    <div className="text-sm text-[var(--ink)]">{s.txt}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-[var(--border)] flex items-center justify-between">
                <div>
                  <div className="text-xs text-[var(--ink-soft)]">Método de pagamento</div>
                  <div className="font-semibold text-[var(--ink)]">Cartão Dá</div>
                </div>
                <div className="chip chip-terra">15 € · Cartão Dá</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-3 gap-6 stagger">
          {[
            { icon: Share2, title: "Partilhe com um clique", body: "Cada vaga tem um link e QR Code únicos. Envie por WhatsApp, Telegram ou email — a origem é registada automaticamente." },
            { icon: ShieldCheck, title: "Sem letra pequena", body: "Protocolo assinado digitalmente, com registo de IP, data e hora. Regras claras sobre exclusões e período mínimo." },
            { icon: HandCoins, title: "Pagamento no dia certo", body: "Prémios aprovados durante um mês são pagos até ao dia 8 do mês seguinte. Sem chatices." },
          ].map((f) => (
            <div key={f.title} className="card-surface p-8">
              <div className="w-11 h-11 rounded-xl bg-[var(--muted)] grid place-items-center text-[var(--forest)] mb-5">
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="font-heading text-2xl text-[var(--ink)]">{f.title}</h3>
              <p className="text-[var(--ink-soft)] mt-2 text-sm leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <SpotlightSection />
      <LeaderboardSection />

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="card-surface p-10 md:p-14 relative overflow-hidden">
          <div className="absolute -right-24 -bottom-24 w-72 h-72 rounded-full bg-[rgba(217,108,74,0.15)] blur-3xl" aria-hidden />
          <div className="grid md:grid-cols-2 gap-8 items-center relative">
            <div>
              <div className="eyebrow">Como funciona</div>
              <h2 className="font-heading text-4xl sm:text-5xl mt-3 text-[var(--ink)]">Um fluxo, oito etapas transparentes.</h2>
              <p className="text-[var(--ink-soft)] mt-4 leading-relaxed">
                Do momento em que recomenda até ao pagamento, cada estado é visível no seu painel — sem black boxes.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {["Recomendado","Entrevista","Selecionado","Iniciou funções","Período mínimo","Prémio aprovado","Pagamento agendado","Pago"].map((s, i) => (
                <div key={s} className="flex items-center gap-3 p-3 rounded-xl bg-[var(--muted)]">
                  <div className="w-7 h-7 rounded-full bg-[var(--forest)] text-white text-xs grid place-items-center font-semibold">{i+1}</div>
                  <div className="text-sm font-medium text-[var(--ink)]">{s}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[var(--border)] py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-between gap-4 text-sm text-[var(--ink-soft)]">
          <div>© {new Date().getFullYear()} CARING · Programa de Referenciação</div>
          <div className="flex items-center gap-4">
            <Link to="/login">Entrar</Link>
            <Link to="/register">Aderir</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
